import React, { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import moment from "moment";
import { studentControler } from "../../_api/controllers/student.controller.ts";
import { personalController } from "../../_api/controllers/personal.controller.ts";
import { paymentController } from "../../_api/controllers/payment.controller.ts";
import { depenseController } from "../../_api/controllers/depense.controller.ts";
import { studentAttendanceController } from "../../_api/controllers/attendance.controller.ts";
import { parentController } from "../../_api/controllers/parent.controller.ts";
import {
  classeController,
  motifPaiementController,
  promotionController,
  schoolController,
  schoolYearController,
} from "../../_api/controllers/scolarite.controller.ts";
import { userController } from "../../_api/controllers/user.controller.ts";

type PreloadTask = {
  label: string;
  queryKey: readonly unknown[];
  queryFn: () => Promise<unknown>;
};

const buildTasks = (): PreloadTask[] => {
  const date = moment().format("YYYY-MM-DD");
  return [
    {
      label: "Années scolaires",
      queryKey: ["schoolYears"],
      queryFn: schoolYearController.getAll,
    },
    {
      label: "Établissement",
      queryKey: ["school"],
      queryFn: schoolController.getAll,
    },
    {
      label: "Élèves",
      queryKey: ["students"],
      queryFn: studentControler.getAll,
    },
    {
      label: "Promotions",
      queryKey: ["promotions"],
      queryFn: promotionController.getAll,
    },
    {
      label: "Classes",
      queryKey: ["classes"],
      queryFn: classeController.getAll,
    },
    {
      label: "Utilisateurs",
      queryKey: ["users"],
      queryFn: userController.getAll,
    },
    {
      label: "Personnel",
      queryKey: ["personals"],
      queryFn: personalController.getAll,
    },
    {
      label: "Paiements",
      queryKey: ["payments"],
      queryFn: paymentController.getAll,
    },
    {
      label: "Motifs de paiement",
      queryKey: ["motifPaiement"],
      queryFn: motifPaiementController.getAll,
    },
    {
      label: "Dépenses",
      queryKey: ["depenses"],
      queryFn: depenseController.getAll,
    },
    {
      label: "Présences du mois",
      queryKey: ["studentAttendance"],
      queryFn: () => studentAttendanceController.getAllByMonth(date),
    },
    {
      label: "Parents",
      queryKey: ["parents"],
      queryFn: parentController.getAll,
    },
    {
      label: "Année scolaire active",
      queryKey: ["activeSchoolYears"],
      queryFn: schoolYearController.getActive,
    },
  ];
};

// Keep prefetched results fresh when the Dashboard mounts; otherwise React Query
// refetches immediately with the default staleTime=0.
async function fetchTask(client: QueryClient, task: PreloadTask) {
  client.setQueryDefaults(task.queryKey, { staleTime: 60_000 });
  return client.fetchQuery({
    queryKey: task.queryKey,
    queryFn: task.queryFn,
    staleTime: 60_000,
    retry: 1,
  });
}

type Props = { onComplete: () => void };

const DashboardPreloader: React.FC<Props> = ({ onComplete }) => {
  const client = useQueryClient();
  const tasksRef = useRef<PreloadTask[]>(buildTasks());
  const completedRef = useRef<Set<string>>(new Set());
  const runningRef = useRef(false);
  const finishedRef = useRef(false);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const [done, setDone] = useState(0);
  const [failed, setFailed] = useState<string[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [currentLabel, setCurrentLabel] = useState(
    "Préparation de votre espace...",
  );

  const run = useCallback(async () => {
    if (runningRef.current || finishedRef.current) return;
    runningRef.current = true;
    setIsRunning(true);
    setFailed([]);

    const pending = tasksRef.current.filter(
      (task) => !completedRef.current.has(JSON.stringify(task.queryKey)),
    );

    const outcomes = await Promise.allSettled(
      pending.map(async (task) => {
        const key = JSON.stringify(task.queryKey);
        await fetchTask(client, task);
        completedRef.current.add(key);
        setDone(completedRef.current.size);
        setCurrentLabel(`${task.label} chargé`);
      }),
    );

    const errors = outcomes.flatMap((outcome, i) =>
      outcome.status === "rejected" ? [pending[i].label] : [],
    );
    runningRef.current = false;
    setIsRunning(false);

    if (errors.length) {
      setFailed(errors);
      return;
    }
    finishedRef.current = true;
    onCompleteRef.current();
  }, [client]);

  useEffect(() => {
    void run();
  }, [run]);

  const percent = Math.round((done / tasksRef.current.length) * 100);

  return (
    <div
      className="fixed inset-0 z-[9999] flex min-h-screen items-center justify-center overflow-auto bg-white px-5 py-8"
      role="status"
      aria-live="polite"
    >
      <div className="flex w-full max-w-[360px] flex-col items-center text-center">
        <div className="mb-7 flex h-[94px] w-[94px] items-center justify-center rounded-2xl bg-blue-300 p-3">
          <img
            src="/images/chiksoft.png"
            alt="Chiksoft"
            className="h-full w-full object-contain"
          />
        </div>
        <h1 className="text-xl font-bold tracking-tight text-[#17233E]">
          Chiksoft
        </h1>
        <p className="mt-2 text-[12px] text-slate-500">
          {failed.length
            ? "Certaines données n'ont pas pu être chargées."
            : "Chargement de votre espace de travail..."}
        </p>
        <div className="mt-7 w-full">
          <div
            className="h-[4px] w-full overflow-hidden rounded-full bg-slate-100"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={percent}
          >
            <div
              className="h-full rounded-full bg-blue-600 transition-[width] duration-300 ease-out"
              style={{ width: `${percent}%` }}
            />
          </div>
          <div className="mt-3 flex items-center justify-between text-[11px] text-slate-500">
            <span className="truncate pr-4">
              {failed.length ? "Chargement interrompu" : currentLabel}
            </span>
            <span className="shrink-0 font-semibold tabular-nums text-blue-600">
              {percent}%
            </span>
          </div>
        </div>
        {failed.length > 0 && (
          <div className="mt-6 w-full rounded-xl border border-red-100 bg-red-50 p-4 text-left">
            <p className="text-[12px] font-semibold text-red-700">
              Échec du chargement
            </p>
            <p className="mt-1 text-[11px] text-red-600">{failed.join(", ")}</p>
            <button
              type="button"
              disabled={isRunning}
              onClick={() => void run()}
              className="mt-3 inline-flex h-9 items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 text-[11px] font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
            >
              <i className="fas fa-rotate-right" /> Réessayer
            </button>
          </div>
        )}
        <p className="mt-9 text-[10px] text-slate-400">
          © {new Date().getFullYear()} Ehula Technologies
        </p>
      </div>
    </div>
  );
};

export default DashboardPreloader;
